import React, { createContext, useState, useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { setActiveCurrency, ZERO_DECIMAL_CURRENCIES } from '../utils/currency';
import { applyRegionalText } from '../utils/regionalText';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export const LocalizationContext = createContext();

export const useLocalization = () => {
  const context = useContext(LocalizationContext);
  if (!context) {
    throw new Error('useLocalization must be used within LocalizationProvider');
  }
  return context;
};

// Colombia/COP is this app's convention default everywhere else in the
// codebase (product API, sitemap, OG tags, ...) — used here as the
// country's INITIAL state so the whole storefront (categories, hero
// slider, featured products — everything gates on `countryCode`) can
// render on the very first paint instead of staying blank. The async
// detection below still runs and correctly upgrades this if a saved
// preference or a different detected country applies; this is just what
// shows while that's in flight, not a replacement for it.
const DEFAULT_COUNTRY = {
  code: 'CO',
  name: 'Colombia',
  language: 'es',
  languageName: 'Español',
  currency: 'COP',
  currencySymbol: '$',
};

export const LocalizationProvider = ({ children }) => {
  const { i18n } = useTranslation();
  const [country, setCountry] = useState(DEFAULT_COUNTRY);
  const [availableCountries, setAvailableCountries] = useState([]);
  const [loading, setLoading] = useState(true);

  // Idempotent module-level value; set during render so children format with the right currency on first paint.
  setActiveCurrency(country?.currency, country ? `${country.language}-${country.code}` : undefined);

  // Load saved preference or default
  useEffect(() => {
    loadLocalization();
  }, []);

  // Update i18n language when country changes
  useEffect(() => {
    if (country && country.language) {
      i18n.changeLanguage(country.language);
      applyRegionalText(i18n, country.code, country.language);
    }
  }, [country, i18n]);

  const loadLocalization = async () => {
    try {
      // Fetch available countries FIRST
      const countriesResponse = await axios.get(`${API}/api/localization/countries`);
      const countries = countriesResponse.data;
      setAvailableCountries(countries);
      

      // A ?country=XX link (Google Shopping listings, shared links) wins over everything else, so the
      // landing page shows that country's price and language — and the choice sticks while browsing.
      const urlCountryCode = (new URLSearchParams(window.location.search).get('country') || '').toUpperCase();
      const urlCountry = urlCountryCode && countries.find(c => c.code === urlCountryCode && c.isEnabled);
      if (urlCountry) {
        localStorage.setItem('selectedCountry', urlCountry.code);
        setCountry(urlCountry);
        return;
      }

      // Check localStorage for saved preference
      const savedCountryCode = localStorage.getItem('selectedCountry');
      
      if (savedCountryCode) {
        // User has explicitly selected a country before
        try {
          const countryResponse = await axios.get(`${API}/api/localization/countries/${savedCountryCode}`);
          setCountry(countryResponse.data);
          return;
        } catch (error) {
        }
      }

      // No saved preference - auto-detect from IP
      try {
        const geoResponse = await axios.get(`${API}/api/geolocation/detect-country`);
        const detectedCountryCode = geoResponse.data.countryCode;
        

        if (detectedCountryCode && !geoResponse.data.isLocal) {
          // Check if detected country is in our enabled countries
          const matchedCountry = countries.find(c => c.code === detectedCountryCode && c.isEnabled);
          
          if (matchedCountry) {
            // User's country is supported!
            const selectedCountry = {
              code: matchedCountry.code,
              name: matchedCountry.name,
              language: matchedCountry.language,
              languageName: matchedCountry.languageName,
              currency: matchedCountry.currency,
              currencySymbol: matchedCountry.currencySymbol
            };
            setCountry(selectedCountry);
            return;
          } else {
          }
        }
      } catch (geoError) {
      }

      // Fallback: Use first enabled country
      await loadDefaultCountry(countries);
      
    } catch (error) {
      console.error('Error loading localization:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadDefaultCountry = async (countries = null) => {
    try {
      // Use provided countries list or availableCountries state
      const countryList = countries || availableCountries;
      
      // First, try to get the first enabled country from available countries
      if (countryList && countryList.length > 0) {
        const firstEnabledCountry = countryList.find(c => c.isEnabled) || countryList[0];
        const defaultCountry = {
          code: firstEnabledCountry.code,
          name: firstEnabledCountry.name,
          language: firstEnabledCountry.language,
          languageName: firstEnabledCountry.languageName,
          currency: firstEnabledCountry.currency,
          currencySymbol: firstEnabledCountry.currencySymbol
        };
        setCountry(defaultCountry);
        return;
      }
      
      // Fallback: try the backend default endpoint (only if no countries available)
      const response = await axios.get(`${API}/api/localization/default`);
      const defaultCountry = {
        code: response.data.countryCode,
        name: response.data.countryName,
        language: response.data.language,
        languageName: response.data.languageName,
        currency: response.data.currency,
        currencySymbol: response.data.currencySymbol
      };
      setCountry(defaultCountry);
    } catch (error) {
      console.error('Error loading default country:', error);
    }
  };

  const changeCountry = async (countryCode) => {
    try {
      const response = await axios.get(`${API}/api/localization/countries/${countryCode}`);
      setCountry(response.data);
      localStorage.setItem('selectedCountry', countryCode);
      
      // Reload page to apply changes
      window.location.reload();
    } catch (error) {
      console.error('Error changing country:', error);
    }
  };

  // Format price with current currency, always followed by the ISO code ("$150 USD", "$280.000 COP").
  const formatPrice = (price, options = {}) => {
    if (!country) return `$${typeof price === 'number' ? price.toFixed(2) : '0.00'}`;

    const { showSymbol = true, decimals = 2 } = options;
    const locale = country.currency === 'COP' ? 'es-CO' : `${country.language}-${country.code}`;

    const raw = typeof price === 'object' && price !== null
      ? (price[country.currency] || price.USD || 0)
      : price;
    const numPrice = Number(raw) || 0;
    // Zero-decimal currencies (COP, CLP, PYG...) never show cents; whole amounts elsewhere drop the ".00"
    const effectiveDecimals = ZERO_DECIMAL_CURRENCIES.has(country.currency) || Number.isInteger(numPrice) ? 0 : decimals;

    let formatted;
    try {
      formatted = numPrice.toLocaleString(locale, { minimumFractionDigits: effectiveDecimals, maximumFractionDigits: effectiveDecimals });
    } catch (e) {
      formatted = numPrice.toLocaleString('en-US', { minimumFractionDigits: effectiveDecimals, maximumFractionDigits: effectiveDecimals });
    }
    if (!showSymbol) return formatted;
    return `${country.currencySymbol}${formatted} ${country.currency}`;
  };

  // Get price for current currency
  const getPrice = (prices) => {
    if (!country) return 0;
    
    if (typeof prices === 'object' && prices !== null) {
      return Number(prices[country.currency] || prices.USD || 0);
    }
    
    return Number(prices) || 0;
  };

  // Get translated text for current language
  const getTranslation = (translatedText) => {
    if (!country || !translatedText) return '';
    
    if (typeof translatedText === 'object') {
      return translatedText[country.language] || translatedText.en || '';
    }
    
    return translatedText;
  };

  const value = {
    country,
    availableCountries,
    loading,
    changeCountry,
    formatPrice,
    getPrice,
    getTranslation,
    currency: country?.currency,
    currencySymbol: country?.currencySymbol,
    language: country?.language,
    languageName: country?.languageName,
    countryCode: country?.code,
    countryName: country?.name,
  };

  return (
    <LocalizationContext.Provider value={value}>
      {children}
    </LocalizationContext.Provider>
  );
};
